import type MediasoupClient from 'mediasoup-client';
import axios from 'axios';
import React from 'react';
import {
  Button,
  Dimmer,
  DropdownProps,
  Icon,
  Loader,
  Modal,
} from 'semantic-ui-react';
import io, { Socket } from 'socket.io-client';
import {
  formatSpeed,
  iceServers,
  isMobile,
  serverPath,
  testAutoplay,
  openFileSelector,
  getAndSaveClientId,
  calculateMedian,
  getUserImage,
  isYouTube,
  isMagnet,
  isHttp,
  isHls,
  isScreenShare,
  isFileShare,
  isVBrowser,
} from '../../utils';
import { generateName } from '../../utils/generateName';
import { Chat } from '../Chat';
import { TopBar } from '../TopBar';
import { VBrowser } from '../VBrowser';
import { VideoChat } from '../VideoChat';
import { getCurrentSettings } from '../Settings';
import { MultiStreamModal } from '../Modal/MultiStreamModal';
import { ComboBox } from '../ComboBox/ComboBox';
import { SearchComponent } from '../SearchComponent/SearchComponent';
import { Controls } from '../Controls/Controls';
import { VBrowserModal } from '../Modal/VBrowserModal';
import { SettingsTab } from '../Settings/SettingsTab';
import { ErrorModal } from '../Modal/ErrorModal';
import { PasswordModal } from '../Modal/PasswordModal';
import { ScreenShareModal } from '../Modal/ScreenShareModal';
import { FileShareModal } from '../Modal/FileShareModal';
import firebase from 'firebase/compat/app';
import { SubtitleModal } from '../Modal/SubtitleModal';
import { HTML } from './HTML';
import { YouTube } from './YouTube';
import type WebTorrent from 'webtorrent';
import { getJwtToken } from '../../utils/auth';
import { checkRoomAccess, getWatchParty, startParty } from '../../api/user';
import {
  RoomContext,
  RoomContextType,
} from '../../contexts/RoomContextProvider';
import WatchPartyEndScreen from '../endScreen/WatchPartyEndScreen';
import HostLeaveDialog from '../HostLeaveDialog/HostLeaveDialog';
import LeaveDialog from '../HostLeaveDialog/LeaveDialog';
import Playlist from '../ComboBox/Playlist';
import 'react-quill/dist/quill.snow.css';
import DomPurify from 'dompurify';

declare global {
  interface Window {
    onYouTubeIframeAPIReady: any;
    YT: YT.JsApi;
    watchparty: {
      ourStream: MediaStream | undefined;
      videoRefs: HTMLVideoElementDict;
      videoPCs: PCDict;
      webtorrent: WebTorrent.Instance | null;
    };
  }
}

window.watchparty = {
  ourStream: undefined,
  videoRefs: {},
  videoPCs: {},
  webtorrent: null,
};

interface AppProps {
  vanity?: string;
  urlRoomId?: string;
  user?: firebase.User;
  isSubscriber: boolean;
  beta: boolean;
  streamPath: string | undefined;
}

interface AppState {
  state: 'init' | 'starting' | 'connected';
  currentMedia: string;
  currentSubtitle: string;
  currentMediaPaused: boolean;
  participants: User[];
  rosterUpdateTS: Number;
  chat: ChatMessage[];
  playlist: PlaylistVideo[];
  tsMap: NumberDict;
  nameMap: StringDict;
  pictureMap: StringDict;
  myName: string;
  myPicture: string;
  loading: boolean;
  scrollTimestamp: number;
  unreadCount: number;
  fullScreen: boolean;
  fakeFullScreen: boolean;
  controlsTimestamp: number;
  watchOptions: SearchResult[];
  isVBrowser: boolean;
  isAutoPlayable: boolean;
  downloaded: number;
  total: number;
  speed: number;
  connections: number;
  coHosts: string[];
  multiStreamSelection?: {
    name: string;
    url: string;
    length: number;
    playFn?: () => void;
  }[];
  overlayMsg: string;
  isErrorAuth: boolean;
  settings: Settings & RoomSettings;
  vBrowserResolution: string;
  isVBrowserLarge: boolean;
  nonPlayableMedia: boolean;
  currentTab: string;
  isSubscribeModalOpen: boolean;
  isVBrowserModalOpen: boolean;
  isScreenShareModalOpen: boolean;
  isFileShareModalOpen: boolean;
  isSubtitleModalOpen: boolean;
  roomLock: string;
  controller?: string;
  savedPasswords: StringDict;
  roomId: string;
  errorMessage: string;
  successMessage: string;
  warningMessage: string;
  isChatDisabled: boolean;
  showRightBar: boolean;
  owner: string | undefined;
  vanity: string | undefined;
  password: string | undefined;
  roomLink: string;
  roomTitle: string | undefined;
  roomDescription: string | undefined;
  roomTitleColor: string | undefined;
  mediaPath: string | undefined;
  roomPlaybackRate: number;
  isLiveHls: boolean;
  activeTab: string | undefined;
  isWatchPartyEnded: boolean;
  watchPartyDetails: IWatchpartyDetails | undefined;
  isWatchPartyDetailsLoading: boolean;
  allJoinedParticipants: { name: string; uid: string }[];
  selectedMessageIndex: number;
  selectedSubMessageIndex: number;
  isRoomPrivate: boolean | null;
  privateRoomMessage: string | '';
  isChatBoxOpen: boolean;
  showAdminLeaveModal: boolean;
  showLeaveModal: boolean;
  showProgressBar: boolean;
  showPlaylist: boolean;
  isHostorCohost: boolean;
}

export default class App extends React.Component<AppProps, AppState> {
  static contextType = RoomContext;
  state: AppState = {
    state: 'starting',
    currentMedia: '',
    currentMediaPaused: false,
    currentSubtitle: '',
    participants: [],
    rosterUpdateTS: Number(new Date()),
    chat: [],
    playlist: [],
    tsMap: {},
    nameMap: {},
    pictureMap: {},
    myName: '',
    myPicture: '',
    loading: true,
    scrollTimestamp: 0,
    unreadCount: 0,
    fullScreen: false,
    fakeFullScreen: false,
    controlsTimestamp: 0,
    watchOptions: [],
    isVBrowser: false,
    isAutoPlayable: true,
    downloaded: 0,
    total: 0,
    speed: 0,
    connections: 0,
    multiStreamSelection: undefined,
    overlayMsg: '',
    isErrorAuth: false,
    settings: {},
    vBrowserResolution: '1280x720@30',
    isVBrowserLarge: false,
    nonPlayableMedia: false,
    currentTab:
      new URLSearchParams(window.location.search).get('tab') ?? 'chat',
    isSubscribeModalOpen: false,
    isVBrowserModalOpen: false,
    isScreenShareModalOpen: false,
    isFileShareModalOpen: false,
    isSubtitleModalOpen: false,
    roomLock: '',
    controller: '',
    roomId: '',
    savedPasswords: {},
    errorMessage: '',
    successMessage: '',
    warningMessage: '',
    isChatDisabled: false,
    showRightBar: true,
    owner: undefined,
    vanity: undefined,
    password: undefined,
    roomLink: '',
    roomTitle: '',
    roomDescription: '',
    roomTitleColor: '',
    mediaPath: undefined,
    roomPlaybackRate: 0,
    isLiveHls: false,
    activeTab: 'pay',
    coHosts: [],
    isWatchPartyEnded: false,
    watchPartyDetails: undefined,
    isWatchPartyDetailsLoading: true,
    allJoinedParticipants: [],
    selectedMessageIndex: -1,
    selectedSubMessageIndex: -1,
    isRoomPrivate: false,
    privateRoomMessage: '',
    isChatBoxOpen: false,
    showAdminLeaveModal: false,
    showLeaveModal: false,
    showProgressBar: true,
    showPlaylist: false,
    isHostorCohost: false,
  };
  socket: Socket = null as any;
  mediasoupPubSocket: Socket | null = null;
  mediasoupSubSocket: Socket | null = null;
  ytDebounce = true;
  localStreamToPublish?: MediaStream;
  isLocalStreamAFile = false;
  publisherConns: PCDict = {};
  consumerConn?: RTCPeerConnection;
  progressUpdater?: number;
  heartbeat: number | undefined = undefined;
  YouTubeInterface: YouTube = new YouTube(null);
  HTMLInterface: HTML = new HTML('leftVideo');
  pingPongIntervalRef: NodeJS.Timeout | null = null;
  Player = () => {
    if (this.usingYoutube()) {
      return this.YouTubeInterface;
    } else {
      return this.HTMLInterface;
    }
  };

  chatRef = React.createRef<Chat>();

  handleWindowClose(e: BeforeUnloadEvent) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
  }

  async componentDidMount() {
    window.addEventListener('beforeunload', this.handleWindowClose);
    document.onfullscreenchange = this.onFullScreenChange;
    document.onkeydown = this.onKeydown;
    this.getWatchpartyDetails();

    // Send heartbeat to the server
    this.heartbeat = window.setInterval(() => {
      window.fetch(serverPath + '/ping');
    }, 10 * 60 * 1000);

    const canAutoplay = await testAutoplay();
    this.setState({ isAutoPlayable: canAutoplay });
    this.loadSettings();
    setTimeout(() => {
      this.loadYouTube();
    }, 700);
    this.init();
    const interval = setInterval(() => {
      this.socket.emit('ping');
      this.pingPongIntervalRef = interval;
    }, 10000);
  }

  componentWillUnmount() {
    document.removeEventListener('fullscreenchange', this.onFullScreenChange);
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('beforeunload', this.handleWindowClose);
    window.clearInterval(this.heartbeat);
    if (this.pingPongIntervalRef) {
      clearInterval(this.pingPongIntervalRef);
      this.pingPongIntervalRef = null;
    }
  }

  componentDidUpdate(prevProps: AppProps) {
    if (this.props.user && !prevProps.user) {
      this.loadSignInData();
    }
  }

  init = async () => {
    let roomId = '/' + this.props.urlRoomId;
    // if a vanity name, resolve the url to a room id
    if (this.props.vanity) {
      try {
        const response = await axios.get(
          serverPath + '/resolveRoom/' + this.props.vanity
        );
        if (response.data.roomId) {
          roomId = response.data.roomId;
        } else {
          this.setState({ overlayMsg: "Couldn't load this room." });
        }
      } catch (e) {
        console.error(e);
        this.setState({ overlayMsg: "Couldn't load this room." });
        return;
      }
    }
    this.setState({ roomId }, () => {
      this.join(roomId);
    });
  };

  callStartApi = async (roomId: string) => {
    try {
      await startParty(roomId);
    } catch (e) {
      console.error(e);
      this.setState({ overlayMsg: "Couldn't load this room." });
      return;
    }
  };

  join = async (roomId: string) => {
    let password = '';
    try {
      const savedPasswordsString = window.localStorage.getItem(
        'watchparty-passwords'
      );
      const savedPasswords = JSON.parse(savedPasswordsString || '{}');
      this.setState({ savedPasswords });
      password = savedPasswords[roomId] || '';
    } catch (e) {
      console.warn('[ALERT] Could not parse saved passwords');
    }
    const response = await axios.get(serverPath + '/resolveShard' + roomId);
    const shard = Number(response.data) ?? '';
    const uid = this.props.user?.uid;

    const socket = io(serverPath + roomId, {
      transports: ['websocket'],
      // autoConnect: false,
      query: {
        clientId: getAndSaveClientId(),
        password,
        shard,
        uid,
      },
    });
    this.socket = socket;
    socket.on('connect', async () => {
      this.setState({
        state: 'connected',
        overlayMsg: '',
        errorMessage: '',
        successMessage: '',
        warningMessage: '',
      });
      // Load username from localstorage
      let userName = window.localStorage.getItem('watchparty-username');
      this.updateName(null, { value: userName || (await generateName()) });
      this.loadSignInData();
    });
    socket.on('connect_error', (err: any) => {
      console.error(err);
      if (err.message === 'Invalid namespace') {
        this.setState({ overlayMsg: "Couldn't load this room." });
      } else if (err.message === 'not authorized') {
        this.setState({ isErrorAuth: true });
      } else if (err.message === 'room full') {
        this.setState({ overlayMsg: 'This room is full.' });
      }
    });
    socket.on('disconnect', (reason) => {
      if (reason === 'io server disconnect') {
        // the disconnection was initiated by the server, you need to reconnect manually
        this.setState({ overlayMsg: 'Disconnected from server.' });
      } else {
        // else the socket will automatically try to reconnect
        // Use the alert pill since it's less disruptive
        this.setState({ warningMessage: 'Reconnecting...' });
      }
    });
    socket.on('errorMessage', (err: string) => {
      this.setState({ errorMessage: err });
      setTimeout(() => {
        this.setState({ errorMessage: '' });
      }, 3000);
    });
    socket.on('successMessage', (success: string) => {
      this.setState({ successMessage: success });
      setTimeout(() => {
        this.setState({ successMessage: '' });
      }, 3000);
    });
    socket.on('kicked', () => {
      window.removeEventListener('beforeunload', this.handleWindowClose);
      window.location.assign('/');
    });
    // listen to kick broadcasted event
    socket.on('kick', (data: any) => {
      window.removeEventListener('beforeunload', this.handleWindowClose);

      // if kicked user's id is same as current logged in userId
      // then kick the user
      if (uid === data.userToBeKicked) {
        checkRoomAccess(roomId, data.userToBeKicked).then(({ data }) => {
          let message = '';
          if (!data.access) {
            message = data.message;
          }

          this.setState({
            privateRoomMessage: message,
            isRoomPrivate: !data.access,
          });
        });
        // window.location.assign('/');
      }
    });
    socket.on('endParty', () => {
      window.removeEventListener('beforeunload', this.handleWindowClose);
      this.setState({ isWatchPartyEnded: true });
    });
    socket.on('REC:play', () => {
      this.doPlay();
    });
    socket.on('REC:pause', () => {
      this.doPause();
    });
    socket.on('REC:seek', (data: number) => {
      this.syncSelf(data);
    });
    socket.on('REC:playbackRate', (data: number) => {
      this.setState({ roomPlaybackRate: data });
      if (data > 0) {
        this.Player().setPlaybackRate(data);
      }
    });
    socket.on('REC:subtitle', (data: string) => {
      this.setState({ currentSubtitle: data }, () => {
        this.Player().loadSubtitles(data);
      });
    });
    socket.on('REC:changeController', (data: string) => {
      this.setState({ controller: data });
    });
    socket.on('REC:host', async (data: HostState) => {
      let currentMedia = data.video || '';
      if (this.playingScreenShare() && !isScreenShare(currentMedia)) {
        this.stopPublishingLocalStream();
      }
      if (this.playingFileShare() && !isFileShare(currentMedia)) {
        this.stopPublishingLocalStream();
      }
      if (this.playingScreenShare() && isScreenShare(currentMedia)) {
        // Ignore, it's probably a reconnection
        return;
      }
      if (this.playingFileShare() && isFileShare(currentMedia)) {
        // Ignore, it's probably a reconnection
        return;
      }
      if (this.playingVBrowser() && !isVBrowser(currentMedia)) {
        this.stopVBrowser();
      }
      this.setState(
        {
          currentMedia,
          currentMediaPaused: data.paused,
          currentSubtitle: data.subtitle,
          loading: Boolean(data.video),
          nonPlayableMedia: false,
          isVBrowserLarge: data.isVBrowserLarge,
          vBrowserResolution: data.isVBrowserLarge
            ? '1920x1080@30'
            : '1280x720@30',
          controller: data.controller,
          isLiveHls: false,
        },
        async () => {
          const leftVideo = document.getElementById(
            'leftVideo'
          ) as HTMLMediaElement;

          // Stop all players
          // Unless the user is sharing a file, because we play it in leftVideo and capture stream
          if (!this.isLocalStreamAFile) {
            this.HTMLInterface.pauseVideo();
          }
          this.YouTubeInterface.stopVideo();

          if (!this.isLocalStreamAFile) {
            this.Player().clearState();
          }
          if (data.subtitle) {
            this.Player().loadSubtitles(data.subtitle);
          }
          if (data.playbackRate) {
            this.setState({ roomPlaybackRate: data.playbackRate });
            this.Player().setPlaybackRate(data.playbackRate);
          }

          if (
            this.playingScreenShare() ||
            this.playingFileShare() ||
            this.playingVBrowser()
          ) {
            if (!(this.playingVBrowser() && !this.getVBrowserHost())) {
              // Remove the loader unless we're waiting for a vbrowser
              this.setLoadingFalse();
            }
            return;
          }
          if (this.usingYoutube() && !this.YouTubeInterface.isReady()) {
            return;
          }
          const src = data.video;
          const time = data.videoTS;
          if (isMagnet(src)) {
            // WebTorrent
            // magnet:?xt=urn:btih:08ada5a7a6183aae1e09d831df6748d566095a10&dn=Sintel&tr=udp%3A%2F%2Fexplodie.org%3A6969&tr=udp%3A%2F%2Ftracker.coppersurfer.tk%3A6969&tr=udp%3A%2F%2Ftracker.empire-js.us%3A1337&tr=udp%3A%2F%2Ftracker.leechers-paradise.org%3A6969&tr=udp%3A%2F%2Ftracker.opentrackr.org%3A1337&tr=wss%3A%2F%2Ftracker.btorrent.xyz&tr=wss%3A%2F%2Ftracker.fastcast.nz&tr=wss%3A%2F%2Ftracker.openwebtorrent.com&ws=https%3A%2F%2Fwebtorrent.io%2Ftorrents%2F&xs=https%3A%2F%2Fwebtorrent.io%2Ftorrents%2Fsintel.torrent
            // Can't import webtorrent directly yet since it uses importAssertions feature
            const WebTorrent = //@ts-ignore
              (await import('webtorrent/dist/webtorrent.min.js')).default;
            //@ts-ignore
            window.watchparty.webtorrent?._server?.close();
            window.watchparty.webtorrent?.destroy();
            window.watchparty.webtorrent = new WebTorrent();
            await navigator.serviceWorker?.register('/sw.min.js');
            const controller = await navigator.serviceWorker.ready;
            await new Promise((resolve) => setTimeout(resolve, 500));
            // createServer is only in v2, types are outdated
            //@ts-ignore
            const server = await window.watchparty.webtorrent.createServer({
              controller,
            });
            await new Promise((resolve) => setTimeout(resolve, 500));
            await new Promise((resolve) => {
              window.watchparty.webtorrent?.add(
                src,
                {
                  announce: [
                    'wss://tracker.btorrent.xyz',
                    'wss://tracker.openwebtorrent.com',
                  ],
                  destroyStoreOnDestroy: true,
                  maxWebConns: 4,
                  path: '/tmp/webtorrent/',
                  storeCacheSlots: 20,
                  strategy: 'sequential',
                  //@ts-ignore
                  noPeersIntervalTime: 30,
                },
                async (torrent: WebTorrent.Torrent) => {
                  // Torrents can contain many files.
                  const files = torrent.files;
                  const filtered = files.filter(
                    (f: WebTorrent.TorrentFile) => f.length >= 10 * 1024 * 1024
                  );
                  const fileIndex = new URLSearchParams(src).get(
                    'fileIndex'
                  ) as unknown as number;
                  // Try to find a single large file to play
                  const target =
                    files[fileIndex] ??
                    (filtered.length > 1 ? null : filtered[0]);
                  if (!target) {
                    // Open the selector
                    this.launchMultiSelect(
                      files.map((f: WebTorrent.TorrentFile, i: number) => ({
                        name: f.name as string,
                        url: src + `&fileIndex=${i}`,
                        length: f.length as number,
                      }))
                    );
                  } else {
                    //@ts-ignore
                    target.streamTo(leftVideo);
                    leftVideo.currentTime = time;
                  }
                  resolve(null);
                }
              );
            });
          } else if (isHls(src) && window.MediaSource) {
            // Prefer using hls.js if MediaSource Extensions are supported
            // otherwise fallback to native HLS support using video tag (i.e. iPhones)
            // https://moctobpltc-i.akamaihd.net/hls/live/571329/eight/playlist.m3u8
            const Hls = (await import('hls.js')).default;
            let hls = new Hls();
            hls.loadSource(src);
            hls.attachMedia(leftVideo);
            hls.once(Hls.Events.LEVEL_LOADED, (_, data) => {
              this.setState({ isLiveHls: data.details.live });
            });
          } else {
            await this.Player().setSrcAndTime(src, time);
          }
          // Start this video
          if (!data.paused) {
            this.doPlay();
          }
          // One time, when we're ready to play
          leftVideo?.addEventListener(
            'canplay',
            () => {
              this.setLoadingFalse();
              this.syncSelf(this.state.isLiveHls ? data.videoTS : undefined);
              if (data.playbackRate) {
                // Set playback rate again since it might have been lost
                this.Player().setPlaybackRate(data.playbackRate);
              }
            },
            { once: true }
          );

          // Progress updater
          window.clearInterval(this.progressUpdater);
          this.setState({ downloaded: 0, total: 0, speed: 0 });
          if (currentMedia.includes('/stream?torrent=magnet')) {
            this.progressUpdater = window.setInterval(async () => {
              const response = await window.fetch(
                currentMedia.replace('/stream', '/progress')
              );
              const data = await response.json();
              this.setState({
                downloaded: data.downloaded,
                total: data.total,
                speed: data.speed,
                connections: data.connections,
              });
            }, 1000);
          }
          if (isMagnet(currentMedia)) {
            this.progressUpdater = window.setInterval(async () => {
              const client = window.watchparty.webtorrent;
              if (client) {
                this.setState({
                  downloaded: client.torrents[0]?.downloaded,
                  total: client.torrents[0]?.length,
                  speed: client.torrents[0]?.downloadSpeed,
                  connections: client.torrents[0]?.numPeers,
                });
              }
            }, 1000);
          }
        }
      );
    });
    socket.on('REC:chat', (data: ChatMessage) => {
      if (
        !getCurrentSettings().disableChatSound &&
        ((document.visibilityState && document.visibilityState !== 'visible') ||
          this.state.currentTab !== 'chat')
      ) {
        new Audio('/clearly.mp3').play();
      }
      this.state.chat.push(data);
      this.setState({
        chat: this.state.chat,
        scrollTimestamp: Number(new Date()),
        unreadCount:
          this.state.currentTab === 'chat'
            ? this.state.unreadCount
            : this.state.unreadCount + 1,
      });
    });
    socket.on('REC:chatMsg', (data: ChatMessage) => {
      if (
        !getCurrentSettings().disableChatSound &&
        ((document.visibilityState && document.visibilityState !== 'visible') ||
          this.state.currentTab !== 'chat')
      ) {
        new Audio('/clearly.mp3').play();
      }
      let oldChat = [...this.state.chat];
      const dataIndex = oldChat.findIndex((c) => c.uuid === data.uuid);
      if (dataIndex > -1) {
        oldChat[dataIndex] = data;
      } else {
        oldChat.push(data);
      }

      this.setState({
        chat: oldChat,
        scrollTimestamp: Number(new Date()),
        unreadCount:
          this.state.currentTab === 'chat'
            ? this.state.unreadCount
            : this.state.unreadCount + 1,
      });
    });
    socket.on('REC:addReaction', (data: ChatMessage) => {
      // const { chat } = this.state;
      // const msgIndex = chat.findIndex(
      //   (m) => m.id === data.msgId && m.timestamp === data.msgTimestamp
      // );
      // if (msgIndex === -1) {
      //   return;
      // }
      // const msg = chat[msgIndex];
      // msg.reactions = msg.reactions || {};
      // msg.reactions[data.value] = msg.reactions[data.value] || [];
      // msg.reactions[data.value].push(data.user);
      let oldChat = [...this.state.chat];
      let newData = { ...data };

      newData = {
        ...newData,
        isLike: newData.likes?.some((i) => i.uid === this.props.user!.uid),
        replies: newData.replies?.map((rep, ind) => {
          return {
            ...rep,
            isLike: rep.likes?.some((e) => e.uid === this.props.user!.uid),
            replies: rep.replies?.map((subRep, subInd) => {
              return {
                ...subRep,
                isLike: subRep.likes?.some(
                  (l) => l.uid === this.props.user!.uid
                ),
              };
            }),
          };
        }),
      };

      const dataIndex = oldChat.findIndex((c) => c.uuid === newData.uuid);
      if (dataIndex > -1) {
        oldChat[dataIndex] = newData;
      }

      this.setState({ chat: oldChat }, () => {
        // if we add a reaction to the last message we need to scroll down
        // or else the reaction icon might be hidden
        // if (
        //   msgIndex === oldChat.length - 1 &&
        //   this.chatRef.current?.state.isNearBottom
        // ) {
        //   this.chatRef.current?.scrollToBottom();
        // }
      });
    });
    socket.on('REC:removeReaction', (data: ChatMessage) => {
      // const { chat } = this.state;
      // const msg = chat.find(
      //   (m) => m.id === data.msgId && m.timestamp === data.msgTimestamp
      // );
      // if (!msg || !msg.reactions?.[data.value]) {
      //   return;
      // }
      // msg.reactions[data.value] = msg.reactions[data.value].filter(
      //   (id) => id !== data.user
      // );
      // this.setState({ chat });
      let oldChat = [...this.state.chat];
      let newData = { ...data };

      newData = {
        ...newData,
        isLike: newData.likes?.some((i) => i.uid === this.props.user!.uid),
        replies: newData.replies?.map((rep, ind) => {
          return {
            ...rep,
            isLike: rep.likes?.some((e) => e.uid === this.props.user!.uid),
            replies: rep.replies?.map((subRep, subInd) => {
              return {
                ...subRep,
                isLike: subRep.likes?.some(
                  (l) => l.uid === this.props.user!.uid
                ),
              };
            }),
          };
        }),
      };

      const dataIndex = oldChat.findIndex((c) => c.uuid === newData.uuid);
      if (dataIndex > -1) {
        oldChat[dataIndex] = newData;
      }

      this.setState({ chat: oldChat });
    });
    socket.on('REC:tsMap', (data: NumberDict) => {
      this.setState({ tsMap: data }, () => {
        // Dynamic playback rate based on timestamps
        // Disable for sharing types where the users can have different timestamps
        // e.g. screenshare, fileshare, .m3u8 HLS streams
        // Also not necessary for WebRTC sharing since it should be close to realtime
        if (
          !this.state.currentMediaPaused &&
          !this.state.isLiveHls &&
          this.hasDuration() &&
          this.state.roomPlaybackRate === 0 &&
          this.socket.id
        ) {
          const leader = this.getLeaderTime();
          const delta = leader - data[this.socket.id];
          // Set leader pbr to 1
          let pbr = 1;
          // Add .01 pbr for each 100ms delay
          if (delta > 0.5) {
            pbr += Number((delta / 10).toFixed(2));
            pbr = Math.min(pbr, 1.2);
          }
          if (this.Player().getPlaybackRate() !== pbr) {
            this.Player().setPlaybackRate(pbr);
          }
        }
        if (this.state.currentSubtitle) {
          const sharer = this.state.participants.find((p) => p.isScreenShare);
          if (sharer && sharer?.id !== this.socket.id) {
            // Sync only if someone is sharing and it's not us
            const sharerTime = this.state.tsMap[sharer?.id!];
            this.Player().syncSubtitles(sharerTime);
          }
        }
      });
    });
    socket.on('REC:nameMap', (data: StringDict) => {
      this.setState({ nameMap: data });
    });
    socket.on('REC:pictureMap', (data: StringDict) => {
      this.setState({ pictureMap: data });
    });
    socket.on('REC:lock', (data: string) => {
      this.setState({ roomLock: data });
    });
    socket.on('roster', (data: User[]) => {
      this.setState(
        {
          participants: data,
          rosterUpdateTS: Number(new Date()),
        },
        () => {
          this.setupRTCConnections();
        }
      );

      setTimeout(() => {
        const uniqueUids: { name: string; uid: string }[] = [];
        this.state.chat.forEach((i) => {
          if (i.user?.uid) {
            uniqueUids.push({
              name: this.state.nameMap[i.id],
              uid: i.user.uid,
            });
          }
        });
        // for get unique name and uids
        const uniqueById = Array.from(
          new Map(uniqueUids.map((item) => [item.uid, item])).values()
        );

        const updatedUsers = uniqueById.filter((i) =>
          data.some((e) => e.uid === i.uid)
        );

        this.setState({
          allJoinedParticipants: updatedUsers,
        });
      }, 1000);
    });
    socket.on('chatinit', (data: ChatMessage[]) => {
      this.setState({
        chat: data,
        scrollTimestamp: Number(new Date()),
      });
    });
    socket.on('playlist', (data: PlaylistVideo[]) => {
      this.setState({ playlist: data });
    });
    socket.on(
      'signalSS',
      async (data: {
        msg: { ice: any; sdp: any };
        from: string;
        sharer: boolean;
      }) => {
        process.env.NODE_ENV === 'development' && console.log(data);
        // Handle messages received from signaling server
        const msg = data.msg;
        const from = data.from;
        // Determine whether the message came from the sharer or the sharee
        const pc = (
          data.sharer ? this.consumerConn : this.publisherConns[from]
        ) as RTCPeerConnection;
        if (msg.ice !== undefined) {
          pc.addIceCandidate(new RTCIceCandidate(msg.ice));
        } else if (msg.sdp && msg.sdp.type === 'offer') {
          // TODO Currently ios/Safari cannot handle this property, so remove it from the offer
          const _sdp = msg.sdp.sdp
            .split('\n')
            .filter((line: string) => {
              return line.trim() !== 'a=extmap-allow-mixed';
            })
            .join('\n');
          msg.sdp.sdp = _sdp;
          await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
          const answer = await pc.createAnswer();
          // Allow stereo audio
          answer.sdp = answer.sdp?.replace(
            'useinbandfec=1',
            'useinbandfec=1; stereo=1; maxaveragebitrate=510000'
          );
          // Allow multichannel audio if Chromium
          const isChromium = Boolean((window as any).chrome);
          if (isChromium) {
            answer.sdp = answer.sdp
              ?.replace('opus/48000/2', 'multiopus/48000/6')
              .replace(
                'useinbandfec=1',
                'channel_mapping=0,4,1,2,3,5; num_streams=4; coupled_streams=2;maxaveragebitrate=510000;minptime=10;useinbandfec=1'
              );
          }
          await pc.setLocalDescription(answer);
          this.sendSignalSS(from, { sdp: pc.localDescription }, !data.sharer);
        } else if (msg.sdp && msg.sdp.type === 'answer') {
          pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
        }
      }
    );
    socket.on('REC:getRoomState', this.handleRoomState);
    socket.on('CMD:getRoomState', this.handleRoomState);

    setTimeout(() => {
      const uniqueUids: { name: string; uid: string }[] = [];
      this.state.chat.forEach((i) => {
        if (i.user?.uid) {
          uniqueUids.push({ name: this.state.nameMap[i.id], uid: i.user.uid });
        }
      });
      // for get unique name and uids
      const uniqueById = Array.from(
        new Map(uniqueUids.map((item) => [item.uid, item])).values()
      );

      this.setState({
        allJoinedParticipants: uniqueById,
      });
    }, 100);

    window.setInterval(() => {
      if (this.state.currentMedia) {
        this.socket.emit('CMD:ts', this.Player().getCurrentTime());
      }
    }, 1000);
  };

  launchMultiSelect = (
    data?: { name: string; url: string; length: number; playFn?: () => void }[]
  ) => {
    this.setState({ multiStreamSelection: data });
  };

  resetMultiSelect = () => {
    this.setState({ multiStreamSelection: undefined });
  };

  loadSettings = async () => {
    // Load settings from localstorage
    let settings = getCurrentSettings();
    this.setState((prev) => ({ settings: { ...prev.settings, ...settings } }));
  };

  loadSignInData = async () => {
    const user = this.props.user;
    if (user && this.socket) {
      // NOTE: firebase auth doesn't provide the actual first name data that individual providers (G/FB) do
      // It's accessible at the time the user logs in but not afterward
      // If we want accurate surname/given name we'll need to save that somewhere
      const fullName = user.displayName;
      if (fullName) {
        this.updateName(null, { value: fullName });
      }
      const userImage = await getUserImage(user);
      if (userImage) {
        this.updatePicture(userImage);
      }
      this.updateUid(user);
    }
  };

  loadYouTube = () => {
    // This code loads the IFrame Player API code asynchronously.
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    document.body.append(tag);
    window.onYouTubeIframeAPIReady = () => {
      // Note: this fails silently if the element is not available
      const ytPlayer = new window.YT.Player('leftYt', {
        events: {
          onReady: () => {
            this.YouTubeInterface = new YouTube(ytPlayer);
            this.setState({ loading: false });
            // We might have failed to play YT originally, ask for the current video again
            if (this.usingYoutube()) {
              this.socket.emit('CMD:askHost');
            }
          },
          onStateChange: (e: any) => {
            if (
              this.usingYoutube() &&
              e.data === window.YT?.PlayerState?.CUED
            ) {
              this.setState({ loading: false });
              return;
            }
            if (
              this.usingYoutube() &&
              e.data === window.YT?.PlayerState?.ENDED
            ) {
              this.onVideoEnded();
              return;
            }
            if (
              this.ytDebounce &&
              ((e.data === window.YT?.PlayerState?.PLAYING &&
                this.state.currentMediaPaused) ||
                (e.data === window.YT?.PlayerState?.PAUSED &&
                  !this.state.currentMediaPaused))
            ) {
              this.ytDebounce = false;
              if (e.data === window.YT?.PlayerState?.PLAYING) {
                this.socket.emit('CMD:play');
                this.doPlay();
              } else {
                this.socket.emit('CMD:pause');
                this.doPause();
              }
            }
          },
        },
      });
    };
  };

  getWatchpartyDetails = async () => {
    this.setState({ loading: true });
    const roomId = this.props.urlRoomId;
    const res = await getWatchParty(roomId!);
    if (res.status === 200) {
      this.setState({
        isWatchPartyDetailsLoading: false,
        watchPartyDetails: res.data,
        isWatchPartyEnded: res.data.ended_at === null ? false : true,
      });
    }
  };

  // Functions for managing room settings
  getRoomLink = (vanity: string) => {
    if (vanity) {
      return `${window.location.origin}/r/${vanity}`;
    }
    return `${window.location.origin}/watch${this.state.roomId}`;
  };

  // checkingRoomAccess = async () => {
  //   this.setState({ isRoomPrivate: null });
  //   // check if current user is fan or friend of watch party owner
  //   const roomId = this.props.urlRoomId;
  //   const uid = this.props.user?.uid;

  //   if (!uid || !roomId) {
  //     this.setState({ isRoomPrivate: true });
  //     return;
  //   }
  //   // this.setState({ isRoomPrivate: false });
  //   checkRoomAccess(roomId, uid)
  //     .then(({ data }) => {
  //       let message = '';
  //       if (!data.access) {
  //         message = data.message;
  //       }

  //       this.setState(() => ({
  //         isRoomPrivate: !data.access,
  //         privateRoomMessage: message,
  //       }));
  //     })
  //     .catch((error) => {
  //       this.setState({
  //         isRoomPrivate: true,
  //         privateRoomMessage: 'Error while checking room access',
  //       });
  //     });
  // };

  setCoHosts = (coHosts: string[]) => {
    this.setState({ coHosts: coHosts ?? [] });
  };

  handleRoomState = async (data: any) => {
    this.setOwner(data.owner);
    this.setVanity(data.vanity);
    this.setPassword(data.password);
    this.setRoomLink(this.getRoomLink(data.vanity));
    this.setIsChatDisabled(data.isChatDisabled);
    this.setRoomTitle(data.roomTitle);
    this.setRoomDescription(data.roomDescription);
    this.setRoomTitleColor(data.roomTitleColor);
    this.setMediaPath(data.mediaPath);
    this.setSetting(data?.settings);
    this.setCoHosts(data?.co_hosts);
    if (data.owner) {
      await startParty(this.state.roomId);
    }
    window.history.replaceState('', '', this.getRoomLink(data.vanity));
  };

  setOwner = (owner: string) => {
    this.setState({ owner });
  };
  setVanity = (vanity: string | undefined) => {
    this.setState({ vanity });
  };
  setPassword = (password: string | undefined) => {
    this.setState({ password });
  };
  setRoomLink = (roomLink: string) => {
    this.setState({ roomLink });
  };
  setRoomTitle = (roomTitle: string | undefined) => {
    this.setState({ roomTitle });
  };
  setRoomDescription = (roomDescription: string | undefined) => {
    this.setState({ roomDescription });
  };
  setRoomTitleColor = (roomTitleColor: string | undefined) => {
    this.setState({ roomTitleColor });
  };
  setMediaPath = (mediaPath: string | undefined) => {
    this.setState({ mediaPath });
  };
  setSetting = (settings: RoomSettings) => {
    this.setState((prev) => ({ settings: { ...prev.settings, ...settings } }));
  };

  setRoomLock = async (locked: boolean) => {
    const uid = this.props.user?.uid;
    const token = getJwtToken();
    this.socket.emit('CMD:lock', { uid, token, locked });
  };

  haveLock = () => {
    if (!this.state.roomLock) {
      return true;
    }
    return this.props.user?.uid === this.state.roomLock;
  };

  setIsChatDisabled = (val: boolean) => this.setState({ isChatDisabled: val });

  clearChat = async () => {
    const uid = this.props.user?.uid;
    const token = await this.props.user?.getIdToken();
    this.socket.emit('CMD:deleteChatMessages', { uid, token });
  };

  startFileShare = async (useMediaSoup: boolean) => {
    const files = await openFileSelector();
    if (!files) {
      return;
    }
    const file = files[0];
    this.Player().clearState();
    const leftVideo = document.getElementById('leftVideo') as HTMLMediaElement;
    leftVideo.src = URL.createObjectURL(file);
    leftVideo.play();
    //@ts-ignore
    this.localStreamToPublish = leftVideo?.captureStream();
    this.isLocalStreamAFile = true;
    if (this.localStreamToPublish) {
      this.socket.emit('CMD:joinScreenShare', {
        file: true,
        mediasoup: useMediaSoup,
      });
    }
  };

  startScreenShare = async (useMediaSoup: boolean) => {
    if (!navigator.mediaDevices.getDisplayMedia) {
      console.error('Screen sharing not supported on this device.');
      alert('Screen sharing not supported on this device.');
      return;
    }

    try {
      const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          width: isMobile ? undefined : 1280,
          height: isMobile ? undefined : 720,
          ...(isMobile ? {} : { logicalSurface: true }),
        },
        audio: isMobile
          ? false // mobile audio capture not reliable
          : {
              autoGainControl: false,
              channelCount: 2,
              echoCancellation: false,
              noiseSuppression: false,
              sampleRate: 48000,
              sampleSize: 16,
            },
      });

      this.localStreamToPublish = stream;
      this.isLocalStreamAFile = false;

      this.socket.emit('CMD:joinScreenShare', {
        file: false,
        mediasoup: useMediaSoup,
      });

      this.setState({ showProgressBar: false });
    } catch (err) {
      alert('Failed to start screen share');
      console.error('Failed to start screen share:', err);
    }
  };

  // Share the video to mediasoup
  publishMediasoup = async (mediasoupURL: string) => {
    const localStream = this.localStreamToPublish;
    let device: MediasoupClient.types.Device = null as any;
    let producerTransport: MediasoupClient.types.Transport = null as any;

    // =========== socket.io ==========
    const connectSocket = (mediasoupURL: string) => {
      return new Promise<void>((resolve, reject) => {
        this.mediasoupPubSocket = io(mediasoupURL, {
          transports: ['websocket'],
        });

        const socket = this.mediasoupPubSocket;
        socket?.on('connect', function () {
          resolve();
        });
        socket?.on('error', function (err) {
          reject(err);
        });
      });
    };

    const sendRequest = (type: string, data: any) => {
      return new Promise<any>((resolve, reject) => {
        const socket = this.mediasoupPubSocket;
        socket?.emit(type, data, (err: any, response: any) => {
          if (!err) {
            // Success response, so pass the mediasoup response to the local Room.
            resolve(response);
          } else {
            reject(err);
          }
        });
      });
    };

    async function publish() {
      // --- get transport info ---
      const params = await sendRequest('createProducerTransport', {});
      producerTransport = device.createSendTransport(params);

      // --- join & start publish --
      producerTransport.on(
        'connect',
        async (
          {
            dtlsParameters,
          }: { dtlsParameters: MediasoupClient.types.DtlsParameters },
          callback: () => void,
          errback: (error: Error) => void
        ) => {
          sendRequest('connectProducerTransport', {
            dtlsParameters: dtlsParameters,
          })
            .then(callback)
            .catch(errback);
        }
      );

      producerTransport.on(
        'produce',
        async (
          {
            kind,
            rtpParameters,
          }: {
            kind: string;
            rtpParameters: MediasoupClient.types.RtpParameters;
          },
          callback: ({ id }: { id: string }) => void,
          errback: (error: Error) => void
        ) => {
          try {
            const { id } = await sendRequest('produce', {
              transportId: producerTransport.id,
              kind,
              rtpParameters,
            });
            callback({ id });
          } catch (err: any) {
            errback(err);
          }
        }
      );

      // producerTransport.on('connectionstatechange', (state: string) => {
      //   switch (state) {
      //     case 'connecting':
      //       break;

      //     case 'connected':
      //       break;

      //     case 'failed':
      //       producerTransport.close();
      //       break;

      //     default:
      //       break;
      //   }
      // });

      const videoTrack = localStream?.getVideoTracks()[0];
      if (videoTrack) {
        const trackParams = { track: videoTrack };
        await producerTransport.produce(trackParams);
      }
      const audioTrack = localStream?.getAudioTracks()[0];
      if (audioTrack) {
        const trackParams = { track: audioTrack };
        await producerTransport.produce(trackParams);
      }
    }

    async function loadDevice(
      routerRtpCapabilities: MediasoupClient.types.RtpCapabilities
    ) {
      const { Device } = await import('mediasoup-client');
      device = new Device();
      await device.load({ routerRtpCapabilities });
    }

    await connectSocket(mediasoupURL);
    // --- get capabilities --
    const data = await sendRequest('getRouterRtpCapabilities', {});
    await loadDevice(data);
    await publish();
  };

  // Play the video from MediaSoup
  subscribeMediasoup = async (mediaSoupURL: string) => {
    let device: MediasoupClient.types.Device = null as any;
    let consumerTransport: MediasoupClient.types.Transport = null as any;
    // =========== socket.io ==========

    const connectSocket = () => {
      return new Promise<void>((resolve, reject) => {
        this.mediasoupSubSocket = io(mediaSoupURL, {
          transports: ['websocket'],
        });
        const socket = this.mediasoupSubSocket;
        socket?.on('connect', function () {
          resolve();
        });
        socket?.on('error', function (err) {
          reject(err);
        });
        socket?.on('newProducer', async function (message) {
          if (consumerTransport) {
            // start consume
            if (message.kind === 'video') {
              await consumeAndResume(message.kind);
            } else if (message.kind === 'audio') {
              await consumeAndResume(message.kind);
            }
          }
        });

        // socket?.on('producerClosed', function (message) {
        //   const localId = message.localId;
        //   const remoteId = message.remoteId;
        //   const kind = message.kind;
        //   if (kind === 'video') {
        //     if (videoConsumer) {
        //       videoConsumer.close();
        //       videoConsumer = null;
        //     }
        //   } else if (kind === 'audio') {
        //     if (audioConsumer) {
        //       audioConsumer.close();
        //       audioConsumer = null;
        //     }
        //   }
        // });
      });
    };

    const sendRequest = (type: string, data: any) => {
      return new Promise<any>((resolve, reject) => {
        const socket = this.mediasoupSubSocket;
        socket?.emit(type, data, (err: Error, response: any) => {
          if (!err) {
            // Success response, so pass the mediasoup response to the local Room.
            resolve(response);
          } else {
            reject(err);
          }
        });
      });
    };

    // =========== media handling ==========
    const addRemoteTrack = (track: MediaStreamTrack) => {
      let video = document.getElementById('leftVideo') as HTMLMediaElement;
      if (video.srcObject) {
        // Track already exists, add it
        (video.srcObject as MediaStream).addTrack(track);
      } else {
        const mediaStream = new MediaStream();
        mediaStream.addTrack(track);
        video.srcObject = mediaStream;
      }
      this.doPlay();
    };

    async function consumeAndResume(kind: string) {
      const consumer = await consume(consumerTransport, kind);
      if (consumer) {
        if (kind === 'video') {
          sendRequest('resume', { kind: kind })
            .then(() => {
              return consumer;
            })
            .catch((err) => {
              return consumer;
            });
        } else {
        }
      } else {
        return null;
      }
    }

    async function loadDevice(
      routerRtpCapabilities: MediasoupClient.types.RtpCapabilities
    ) {
      try {
        const { Device } = await import('mediasoup-client');
        device = new Device();
        await device.load({ routerRtpCapabilities });
      } catch (error: any) {
        if (error.name === 'UnsupportedError') {
        }
      }
    }

    async function consume(
      transport: MediasoupClient.types.Transport,
      trackKind: string
    ) {
      const { rtpCapabilities } = device;
      const data = await sendRequest('consume', {
        rtpCapabilities: rtpCapabilities,
        kind: trackKind,
      }).catch((err) => {
        console.error('SUBSCRIBE: ERROR:', err);
      });
      const { producerId, id, kind, rtpParameters } = data;

      if (producerId) {
        let codecOptions = {};
        const consumer = await transport.consume({
          id,
          producerId,
          kind,
          rtpParameters,
          //@ts-ignore
          codecOptions,
        });

        addRemoteTrack(consumer.track);
        return consumer;
      } else {
        return null;
      }
    }

    async function subscribe() {
      const params = await sendRequest('createConsumerTransport', {});
      consumerTransport = device.createRecvTransport(params);

      // --- join & start watching
      consumerTransport.on(
        'connect',
        async (
          {
            dtlsParameters,
          }: { dtlsParameters: MediasoupClient.types.DtlsParameters },
          callback: () => void,
          errback: (err: Error) => void
        ) => {
          sendRequest('connectConsumerTransport', {
            dtlsParameters: dtlsParameters,
          })
            .then(callback)
            .catch(errback);
        }
      );

      // consumerTransport.on('connectionstatechange', (state: string) => {
      //   switch (state) {
      //     case 'connecting':
      //       break;

      //     case 'connected':
      //       break;

      //     case 'failed':
      //       consumerTransport.close();
      //       break;

      //     default:
      //       break;
      //   }
      // });

      await consumeAndResume('video');
      await consumeAndResume('audio');
    }

    // Clear the srcobject so we load our stream when received
    const leftVideo = document.getElementById('leftVideo') as HTMLMediaElement;
    leftVideo.srcObject = null;
    await connectSocket();
    // --- get capabilities --
    const data = await sendRequest('getRouterRtpCapabilities', {});
    await loadDevice(data);
    await subscribe();
  };

  stopPublishingLocalStream = async () => {
    if (this.localStreamToPublish) {
      this.socket.emit('CMD:leaveScreenShare');
      // We don't actually need to unmute if it's a fileshare but this is fine
      this.doSetMute(false);
      this.setState({ showProgressBar: true });
    }
    this.localStreamToPublish &&
      this.localStreamToPublish.getTracks().forEach((track) => {
        track.stop();
      });
    this.localStreamToPublish = undefined;
    if (this.consumerConn) {
      this.consumerConn.close();
      this.consumerConn = undefined;
    }
    Object.values(this.publisherConns).forEach((pc) => {
      pc.close();
    });
    this.publisherConns = {};
    this.isLocalStreamAFile = false;
    if (this.mediasoupPubSocket) {
      this.mediasoupPubSocket.close();
      this.mediasoupPubSocket = null;
    }
    if (this.mediasoupSubSocket) {
      this.mediasoupSubSocket.close();
      this.mediasoupSubSocket = null;
    }
  };

  setupRTCConnections = async () => {
    if (!this.playingScreenShare() && !this.playingFileShare()) {
      return;
    }
    const sharer = this.state.participants.find((p) => p.isScreenShare);
    const selfId = getAndSaveClientId();
    const localTrack = this.localStreamToPublish?.getVideoTracks()[0];
    if (localTrack && !localTrack.onended) {
      // Stop sharing if the local stream stops
      localTrack.onended = () => this.stopPublishingLocalStream();
    }
    if (this.state.currentMedia.includes('@')) {
      let prefix = 'screenshare://';
      if (this.playingFileShare()) {
        prefix = 'fileshare://';
      }
      const unprefixed = this.state.currentMedia.replace(prefix, '');
      const mediasoupURL = unprefixed.split('@')[1];
      if (sharer?.clientId === selfId && this.mediasoupPubSocket == null) {
        await this.publishMediasoup(mediasoupURL);
      }
      // If we're not sharing a file, also start watching
      // avoid duplicate watching if the socket already exists
      if (!this.isLocalStreamAFile && this.mediasoupSubSocket == null) {
        await this.subscribeMediasoup(mediasoupURL);
      }
      return;
    }

    // We're the sharer, create a connection to each other member
    if (sharer?.clientId === selfId) {
      // Delete and close any connections that aren't in the current member list (maybe someone disconnected)
      // This allows them to rejoin later
      const clientIds = new Set(
        this.state.participants.map((p) => p?.clientId)
      );
      Object.entries(this.publisherConns).forEach(([key, value]) => {
        if (!clientIds.has(key)) {
          value.close();
          delete this.publisherConns[key];
        }
      });

      this.state.participants.forEach((user) => {
        const id = user?.clientId!;
        if (id === selfId && this.isLocalStreamAFile) {
          // Don't set up a connection to ourselves if sharing file
          return;
        }
        if (!this.publisherConns[id]) {
          // Set up the RTCPeerConnection for sharing media to each member
          const pc = new RTCPeerConnection({ iceServers: iceServers() });
          this.publisherConns[id] = pc;
          this.localStreamToPublish?.getTracks().forEach((track) => {
            if (this.localStreamToPublish != null) {
              pc.addTrack(track, this.localStreamToPublish);
            }
          });
          pc.onicecandidate = (event) => {
            // We generated an ICE candidate, send it to peer
            if (event.candidate) {
              this.sendSignalSS(id, { ice: event.candidate }, true);
            }
          };
          pc.onnegotiationneeded = async () => {
            // Start connection for peer's video
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            this.sendSignalSS(id, { sdp: pc.localDescription }, true);
          };
        }
      });
    }
    // We're a watcher, establish connection to sharer
    // If screensharing, sharer also does this
    // If filesharing, sharer does not do this since we use leftVideo
    if (sharer && !this.consumerConn && !this.isLocalStreamAFile) {
      const pc = new RTCPeerConnection({ iceServers: iceServers() });
      this.consumerConn = pc;
      pc.onicecandidate = (event) => {
        // We generated an ICE candidate, send it to sharer
        if (event.candidate) {
          this.sendSignalSS(sharer?.clientId!, { ice: event.candidate });
        }
      };
      pc.ontrack = (event: RTCTrackEvent) => {
        // Mount the stream from sharer
        const leftVideo = document.getElementById(
          'leftVideo'
        ) as HTMLMediaElement;
        if (leftVideo) {
          leftVideo.src = '';
          leftVideo.srcObject = event.streams[0];
          this.doPlay();
        }
      };
    }
  };

  startVBrowser = async (rcToken: string, options: { size: string }) => {
    // user.uid is the public user identifier
    // user.getIdToken() is the secret access token we can send to the server to prove identity
    const user = this.props.user;
    const uid = user?.uid;
    const token = await user?.getIdToken();
    this.socket.emit('CMD:startVBrowser', { options, uid, token, rcToken });
  };

  stopVBrowser = async () => {
    this.socket.emit('CMD:stopVBrowser');
  };

  changeController = async (_e: any, data: DropdownProps) => {
    this.socket.emit('CMD:changeController', data.value);
  };

  sendSignalSS = async (to: string, data: any, sharer?: boolean) => {
    this.socket.emit('signalSS', { to, msg: data, sharer });
  };

  usingYoutube = () => {
    return isYouTube(this.state.currentMedia);
  };

  usingNative = () => {
    // Anything that uses HTML Video (e.g. not YouTube, Vimeo, or other embedded JS player)
    return !this.usingYoutube();
  };

  hasDuration = () => {
    // Youtube, link, or magnet, etc. Has a defined runtime (not WebRTC)
    return isHttp(this.state.currentMedia) || isMagnet(this.state.currentMedia);
  };

  playingScreenShare = () => {
    return isScreenShare(this.state.currentMedia);
  };

  playingFileShare = () => {
    return isFileShare(this.state.currentMedia);
  };

  playingVBrowser = () => {
    return isVBrowser(this.state.currentMedia);
  };

  getVBrowserPass = () => {
    return this.state.currentMedia.replace('vbrowser://', '').split('@')[0];
  };

  getVBrowserHost = () => {
    return this.state.currentMedia.replace('vbrowser://', '').split('@')[1];
  };

  isPauseDisabled = () => {
    return this.playingScreenShare() || this.playingVBrowser();
  };

  syncSelf = (customTime?: number) => {
    // Jump to the leader's position, or a custom one
    // for HLS the leader is the live stream position
    let target = customTime ?? this.getLeaderTime();
    if (this.state.isLiveHls) {
      if (customTime) {
        // Translate the time back to video time
        // TODO Safari reports the duration as Infinity, so keep track of our duration using the max of our current timestamp
        const zeroTime =
          Math.floor(Date.now() / 1000) - this.HTMLInterface.getDuration();
        // Cap the time to the leadertime so we don't try to seek too close to edge
        target = Math.min(customTime - zeroTime, this.getLeaderTime());
      } else {
        target = this.getLeaderTime();
      }
    }
    if (target >= 0 && target < Infinity) {
      this.Player().seekVideo(target);
    }
  };

  doPlay = async () => {
    if (!this.state.currentMedia) {
      return;
    }
    const canAutoplay = this.state.isAutoPlayable || (await testAutoplay());
    this.setState(
      { currentMediaPaused: false, isAutoPlayable: canAutoplay },
      async () => {
        if (
          !this.state.isAutoPlayable ||
          (this.localStreamToPublish && !this.isLocalStreamAFile)
        ) {
          this.doSetMute(true);
        } else {
          // this.doSetMute(false);
        }
        try {
          await this.Player().playVideo();
        } catch (e: any) {
          if (e.name === 'NotSupportedError' && this.usingNative()) {
            this.setState({ loading: false, nonPlayableMedia: true });
          }
        }
      }
    );
  };

  doPause = () => {
    this.setState({ currentMediaPaused: true }, async () => {
      this.Player().pauseVideo();
    });
  };

  doSetMute = (muted: boolean) => {
    this.Player().setMute(muted);
    this.refreshControls();
  };

  doSetVolume = (volume: number) => {
    this.Player().setVolume(volume);
  };

  doSubtitle = () => {
    // Native player uses subtitle modal.
    if (this.usingNative()) {
      this.setState({ isSubtitleModalOpen: true });
    }
  };

  doSetPlaybackRate = (rate: number) => {
    // emit an event to the server
    this.socket.emit('CMD:playbackRate', rate);
  };

  togglePlay = () => {
    if (!this.state.currentMedia) return;
    if (!this.haveLock()) {
      return;
    }
    if (this.isPauseDisabled()) {
      return;
    }
    const shouldPlay = this.Player().shouldPlay();
    if (shouldPlay) {
      this.socket.emit('CMD:play');
      this.doPlay();
    } else {
      this.socket.emit('CMD:pause');
      this.doPause();
    }
  };

  togglePlaylist = () => {
    this.setState({ showPlaylist: !this.state.showPlaylist });
  };

  onSeek = (e: any, time: number) => {
    let target = time;
    // Read the time from the click event if it exists
    if (e) {
      const rect = e.target.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const max = rect.width;
      target = (x / max) * this.Player().getDuration();
    }
    target = Math.max(target, 0);
    this.Player().seekVideo(target);
    const hlsTarget =
      Math.floor(Date.now() / 1000) - this.HTMLInterface.getDuration() + target;
    this.socket.emit('CMD:seek', this.state.isLiveHls ? hlsTarget : target);
  };

  onFullScreenChange = async () => {
    const isFs = Boolean(
      document.fullscreenElement || (document as any).webkitFullscreenElement
    );
    this.setState({
      fullScreen: isFs,
      fakeFullScreen: isFs,
    });
  };

  onKeydown = (e: any) => {
    if (!document.activeElement || document.activeElement.tagName === 'BODY') {
      if (e.key === ' ') {
        e.preventDefault();
        this.togglePlay();
      } else if (e.key === 'ArrowRight') {
        this.onSeek(null, this.Player().getCurrentTime() + 15);
      } else if (e.key === 'ArrowLeft') {
        this.onSeek(null, this.Player().getCurrentTime() - 15);
      } else if (e.key === 't') {
        this.fullScreen(false);
      } else if (e.key === 'f') {
        this.fullScreen(true);
      } else if (e.key === 'm') {
        this.toggleMute();
      } else if (e.key === 'Escape') {
        if (this.state.fakeFullScreen) {
          this.setState({ fakeFullScreen: false });
        }
      }
    }
  };

  fullScreen = async (bVideoOnly: boolean) => {
    const isEntering = !this.state.fakeFullScreen;
    this.setState({ fakeFullScreen: isEntering });

    try {
      if (isEntering) {
        const el = document.documentElement as any;
        if (el.requestFullscreen) {
          await el.requestFullscreen();
        } else if (el.webkitRequestFullscreen) {
          await el.webkitRequestFullscreen();
        }
      } else {
        const doc = document as any;
        if (doc.fullscreenElement || doc.webkitFullscreenElement) {
          if (doc.exitFullscreen) {
            await doc.exitFullscreen();
          } else if (doc.webkitExitFullscreen) {
            await doc.webkitExitFullscreen();
          }
        }
      }
    } catch (e) {
      console.warn(
        'Native fullscreen request failed, falling back to CSS only',
        e
      );
    }
  };

  toggleMute = () => {
    this.doSetMute(!this.Player().isMuted());
  };

  setMedia = (_e: any, data: DropdownProps) => {
    let url = data.value;

    if (typeof url === 'string') {
      try {
        const u = new URL(url.trim());

        // Normalize host
        if (
          u.hostname === 'm.youtube.com' ||
          u.hostname === 'youtube.com' ||
          u.hostname === 'music.youtube.com' ||
          u.hostname.includes('youtube-nocookie.com')
        ) {
          u.hostname = 'www.youtube.com';
        }

        // Convert youtu.be to watch?v=
        if (u.hostname === 'youtu.be') {
          const videoId = u.pathname.slice(1);
          u.hostname = 'www.youtube.com';
          u.pathname = '/watch';
          u.search = `?v=${videoId}`;
        }

        // Convert Shorts to watch?v=
        if (u.pathname.startsWith('/shorts/')) {
          const videoId = u.pathname.split('/')[2];
          u.pathname = '/watch';
          u.search = `?v=${videoId}`;
        }

        url = u.toString();
      } catch {
        if (url.includes('youtube') || url.includes('youtu.be')) {
          if (!url.startsWith('https://')) url = 'https://' + url;
          if (!url.includes('www.'))
            url = url.replace('youtube', 'www.youtube');
        }
      }
    }

    this.socket.emit('CMD:host', url);
  };

  playlistAdd = (_e: any, data: DropdownProps) => {
    this.socket.emit('CMD:playlistAdd', data.value);
  };

  playlistMove = (index: number, toIndex: number) => {
    this.socket.emit('CMD:playlistMove', { index, toIndex });
  };

  playlistDelete = (index: number) => {
    this.socket.emit('CMD:playlistDelete', index);
  };

  updateName = (_e: any, data: { value: string }) => {
    this.setState({ myName: data.value });
    this.socket.emit('CMD:name', data.value);
    window.localStorage.setItem('watchparty-username', data.value);
  };

  updatePicture = (url: string) => {
    this.setState({ myPicture: url });
    this.socket.emit('CMD:picture', url);
  };

  updateUid = async (user: firebase.User) => {
    const uid = user.uid;
    const token = getJwtToken();
    this.socket.emit('CMD:uid', { uid, token });
  };

  redirectToWatchPartyDomain = () => {
    window.removeEventListener('beforeunload', this.handleWindowClose);
    if (process.env.REACT_APP_DMDB_DOMAIN) {
      window.location.href = process.env.REACT_APP_DMDB_DOMAIN;
    } else {
      window.location.href = 'https://www.dmdb.com';
    }
  };

  getMediaDisplayName = (input: string) => {
    if (!input) {
      return '';
    }
    // Show the whole URL for youtube
    if (this.usingYoutube()) {
      return input;
    }
    if (input.startsWith('screenshare://')) {
      const sharer = this.state.participants.find((user) => user.isScreenShare);
      return this.state.nameMap[sharer?.id ?? ''] + "'s screen";
    }
    if (input.startsWith('fileshare://')) {
      const sharer = this.state.participants.find((user) => user.isScreenShare);
      return this.state.nameMap[sharer?.id ?? ''] + "'s file";
    }
    if (input.startsWith('vbrowser://')) {
      return 'Virtual Browser' + (this.state.isVBrowserLarge ? '+' : '');
    }
    if (isMagnet(input)) {
      const magnetParsed = new URLSearchParams(input);
      const index = magnetParsed.get('fileIndex');
      return magnetParsed.get('dn') + (index != null ? ` (file ${index})` : '');
    }
    if (input.includes('/stream?torrent=magnet')) {
      const search = new URL(input).search;
      const searchParsed = new URLSearchParams(search);
      const magnetUrl = searchParsed.get('torrent') ?? '';
      const magnetParsed = new URLSearchParams(magnetUrl);
      const index = searchParsed.get('fileIndex');
      return magnetParsed.get('dn') + (index != null ? ` (file ${index})` : '');
    }
    return input;
  };

  setLoadingFalse = () => {
    this.setState({ loading: false });
  };

  getLeaderTime = () => {
    if (this.state.isLiveHls) {
      // Pick a time near the end of the livestream
      return this.HTMLInterface.getDuration() - 5;
    }
    if (this.state.participants.length > 2) {
      return calculateMedian(Object.values(this.state.tsMap));
    }
    return Math.max(...Object.values(this.state.tsMap));
  };

  onVideoEnded = () => {
    const { currentMedia } = this.state;

    this.socket.emit('CMD:playlistNext', currentMedia);
  };

  refreshControls = () => {
    this.setState({ controlsTimestamp: Number(new Date()) });
  };

  removeFromPlaylist = (url: string) => {
    console.log(url, this.state.currentMedia, 'media ');
    this.socket.emit('CMD:playlistDelete', {
      url,
      isRunningVideo: this.state.currentMedia === url,
    });
  };

  render() {
    const { owner } = this.state;
    const uid = this.props.user?.uid;
    const isOwner = uid === owner || this.state.coHosts.includes(uid ?? '');
    const { isRoomPrivate, privateRoomMessage, isHostorCohost } = this
      .context as RoomContextType;

    if (this.state.isWatchPartyDetailsLoading) {
      return (
        <div className="flex items-center justify-center h-dvh">
          <h3 className="text-gradient-pink">Loading....</h3>
        </div>
      );
    }

    if (
      !this.state.isWatchPartyDetailsLoading &&
      this.state.isWatchPartyEnded
    ) {
      return (
        <WatchPartyEndScreen
          watchPartyDetails={this.state.watchPartyDetails}
          totalParticipants={
            Object.values(this.state.nameMap).filter(
              (value, index, self) => self.indexOf(value) === index
            ).length ?? 0
          }
          handleClickOnGoHome={() => {
            window.removeEventListener('beforeunload', this.handleWindowClose);
            if (process.env.REACT_APP_DMDB_DOMAIN) {
              window.location.href = process.env.REACT_APP_DMDB_DOMAIN;
            } else {
              window.location.href = 'http://www.dmdb.com';
            }
          }}
        />
      );
    }

    if (isRoomPrivate === null) {
      return (
        <div className="flex items-center justify-center h-dvh">
          <h3 className="text-gradient-pink">Loading....</h3>
        </div>
      );
    }

    if (isRoomPrivate || this.state.isRoomPrivate) {
      return (
        <div className="flex items-center justify-center h-dvh flex-col">
          <h3 className="text-white text-[18px]">
            {privateRoomMessage ||
              this.state.privateRoomMessage ||
              'This room is private you can not access it.'}
          </h3>
          <button
            className="gradient-btn text-black w-fit p-2 px-4 rounded-xl cursor-pointer mt-4 text-[16px]"
            onClick={() => {
              window.removeEventListener(
                'beforeunload',
                this.handleWindowClose
              );
              if (process.env.REACT_APP_DMDB_DOMAIN) {
                window.location.href = process.env.REACT_APP_DMDB_DOMAIN;
              } else {
                window.location.href = 'http://www.dmdb.com';
              }
            }}
          >
            Home
          </button>
        </div>
      );
    }

    const sharer = this.state.participants.find((p) => p.isScreenShare);
    const controls = (
      <Controls
        key={this.state.controlsTimestamp}
        togglePlay={this.togglePlay}
        onSeek={this.onSeek}
        fullScreen={this.fullScreen}
        toggleMute={this.toggleMute}
        showSubtitle={this.doSubtitle}
        setVolume={this.doSetVolume}
        syncSelf={this.syncSelf}
        paused={this.state.currentMediaPaused}
        muted={this.Player().isMuted()}
        volume={this.Player().getVolume()}
        subtitled={this.Player().isSubtitled()}
        currentTime={this.Player().getCurrentTime()}
        duration={this.Player().getDuration()}
        disabled={!this.haveLock() || !isOwner}
        leaderTime={this.hasDuration() ? this.getLeaderTime() : undefined}
        isPauseDisabled={this.isPauseDisabled()}
        playbackRate={this.Player().getPlaybackRate()}
        setPlaybackRate={this.doSetPlaybackRate}
        beta={this.props.beta}
        roomPlaybackRate={this.state.roomPlaybackRate}
        isYouTube={this.usingYoutube()}
        setSubtitleMode={this.Player().setSubtitleMode}
        isLiveHls={this.state.isLiveHls}
        showProgressBar={this.state.showProgressBar}
        isScreenShare={this.playingScreenShare()}
      />
    );
    const displayRightContent =
      this.state.showRightBar || this.state.fullScreen;
    const watchPartyTitle = this.state.watchPartyDetails?.title ?? '';
    const sanitizedWatchPartyTitle = DomPurify.sanitize(watchPartyTitle);
    const watchPartyTitleTooltip = DomPurify.sanitize(watchPartyTitle, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: [],
    });

    return (
      <React.Fragment>
        {!this.state.isAutoPlayable && !isHostorCohost && (
          <Modal inverted="true" basic open>
            <div className="flex justify-center">
              <Button
                primary
                size="large"
                onClick={() => {
                  this.setState({ isAutoPlayable: true });
                  this.doSetMute(false);
                  this.doSetVolume(1);
                }}
                icon
                labelPosition="left"
              >
                <Icon name="volume up" />
                Click to unmute
              </Button>
            </div>
          </Modal>
        )}
        {this.state.multiStreamSelection && (
          <MultiStreamModal
            streams={this.state.multiStreamSelection}
            setMedia={this.setMedia}
            resetMultiSelect={this.resetMultiSelect}
          />
        )}
        {this.state.isVBrowserModalOpen && (
          <VBrowserModal
            isSubscriber={this.props.isSubscriber}
            closeModal={() => this.setState({ isVBrowserModalOpen: false })}
            startVBrowser={this.startVBrowser}
            user={this.props.user}
            beta={this.props.beta}
          />
        )}
        {this.state.isScreenShareModalOpen && (
          <ScreenShareModal
            beta={this.props.beta}
            isSubscriber={this.props.isSubscriber}
            user={this.props.user}
            closeModal={() => this.setState({ isScreenShareModalOpen: false })}
            startScreenShare={this.startScreenShare}
          />
        )}
        {this.state.isFileShareModalOpen && (
          <FileShareModal
            beta={this.props.beta}
            isSubscriber={this.props.isSubscriber}
            user={this.props.user}
            closeModal={() => this.setState({ isFileShareModalOpen: false })}
            startFileShare={this.startFileShare}
          />
        )}
        {this.state.isSubtitleModalOpen && (
          <SubtitleModal
            closeModal={() => this.setState({ isSubtitleModalOpen: false })}
            socket={this.socket}
            currentSubtitle={this.state.currentSubtitle}
            src={this.state.currentMedia}
            haveLock={this.haveLock}
            getMediaDisplayName={this.getMediaDisplayName}
            beta={this.props.beta}
            setSubtitleMode={this.Player().setSubtitleMode}
            getSubtitleMode={this.Player().getSubtitleMode}
          />
        )}
        {this.state.overlayMsg && <ErrorModal error={this.state.overlayMsg} />}
        {this.state.isErrorAuth && (
          <PasswordModal
            savedPasswords={this.state.savedPasswords}
            roomId={this.state.roomId}
          />
        )}
        {this.state.errorMessage && (
          <div className="fixed inset-0 z-[9999] size-full bg-black/50 grid place-items-center">
            <div className="gradient-modal rounded-xl max-w-xl p-5 text-center w-full">
              <div className="relative">
                <div className="relative size-32 mt-5 mb-7 mx-auto main-box">
                  <div className="first-div size-full rounded-[20%] bg-[#e63757] mx-auto"></div>
                  <div className="second-div size-full rounded-[20%] bg-[#e63757] rotate-45 mx-auto absolute top-0 start-0"></div>
                </div>
                <div className="check-icon absolute top-2/4 start-2/4 -translate-x-2/4 -translate-y-2/4 size-full grid place-items-center">
                  <svg
                    className="size-14"
                    xmlns="http://www.w3.org/2000/svg"
                    width="512"
                    height="512"
                    viewBox="0 0 365.696 365.696"
                  >
                    <g>
                      <path
                        d="M243.188 182.86 356.32 69.726c12.5-12.5 12.5-32.766 0-45.247L341.238 9.398c-12.504-12.503-32.77-12.503-45.25 0L182.86 122.528 69.727 9.374c-12.5-12.5-32.766-12.5-45.247 0L9.375 24.457c-12.5 12.504-12.5 32.77 0 45.25l113.152 113.152L9.398 295.99c-12.503 12.503-12.503 32.769 0 45.25L24.48 356.32c12.5 12.5 32.766 12.5 45.247 0l113.132-113.132L295.99 356.32c12.503 12.5 32.769 12.5 45.25 0l15.081-15.082c12.5-12.504 12.5-32.77 0-45.25zm0 0"
                        fill="#ffffff"
                        opacity="1"
                        data-original="#000000"
                      ></path>
                    </g>
                  </svg>
                </div>
              </div>
              <h3 className="text-gradient-blue text-3xl my-4">
                {this.state.errorMessage}
              </h3>
            </div>
          </div>
        )}

        {this.state.successMessage && (
          <div className="fixed inset-0 z-[9999] size-full bg-black/50 grid place-items-center">
            <div className="gradient-modal rounded-xl max-w-xl p-5 text-center w-full">
              <div className="relative">
                <div className="relative size-32 mt-5 mb-7 mx-auto main-box">
                  <div className="first-div size-full rounded-[20%] bg-green-500 mx-auto"></div>
                  <div className="second-div size-full rounded-[20%] bg-green-500 rotate-45 mx-auto absolute top-0 start-0"></div>
                </div>
                <div className="check-icon absolute top-2/4 start-2/4 -translate-x-2/4 -translate-y-2/4 size-full grid place-items-center">
                  <svg
                    className="size-14"
                    xmlns="http://www.w3.org/2000/svg"
                    width="512"
                    height="512"
                    viewBox="0 0 405.272 405.272"
                  >
                    <g>
                      <path
                        d="M393.401 124.425 179.603 338.208c-15.832 15.835-41.514 15.835-57.361 0L11.878 227.836c-15.838-15.835-15.838-41.52 0-57.358 15.841-15.841 41.521-15.841 57.355-.006l81.698 81.699L336.037 67.064c15.841-15.841 41.523-15.829 57.358 0 15.835 15.838 15.835 41.514.006 57.361z"
                        fill="#ffffff"
                        opacity="1"
                        data-original="#000000"
                      ></path>
                    </g>
                  </svg>
                </div>
              </div>
              <h3 className="text-gradient-blue text-3xl my-4">
                {this.state.successMessage}
              </h3>
            </div>
          </div>
        )}

        {this.state.warningMessage && (
          // <Message
          //   warning
          //   // header={this.state.warningMessage}
          //   content={this.state.warningMessage}
          //   className="fixed top-[10px] left-1/2 -translate-x-1/2 z-[999]"
          // ></Message>
          <div className="bg-black/50 backdrop-filter backdrop-blur-sm w-full h-full fixed inset-0 z-[9999] grid place-items-center">
            <div className="text-center">
              <div className="spinner-border inline-block w-10 h-10 rounded-full text-[#27AE60]"></div>
              <span className="text-white text-[20px] block mt-3">
                {this.state.warningMessage}
              </span>
            </div>
          </div>
        )}

        <div>
          <TopBar
            coHosts={this.state.coHosts}
            handleClickOnNo={this.getWatchpartyDetails}
            participants={this.state.participants}
            socket={this.socket}
            watchPartyDetails={this.state.watchPartyDetails}
            owner={this.state.owner}
            user={this.props.user}
            isSubscriber={this.props.isSubscriber}
            roomTitle={this.state.roomTitle}
            roomDescription={this.state.roomDescription}
            roomTitleColor={this.state.roomTitleColor}
            showInviteButton
            roomId={this.state.roomId}
            showAdminLeaveModal={this.state.showAdminLeaveModal}
            handleToggleAdminLeaveModal={() => {
              this.setState({
                showAdminLeaveModal: !this.state.showAdminLeaveModal,
              });
            }}
            handleToggleLeaveModal={() => {
              this.setState({
                showLeaveModal: !this.state.showLeaveModal,
              });
            }}
            isHostorCohost={isHostorCohost}
          />
          <div className="grid grid-cols-[1fr_400px] gap-4 px-5 pb-5 max-lg:grid-cols-1 h-[calc(100dvh-92px)]">
            <div>
              {this.state.watchPartyDetails?.title ? (
                <div className="grid grid-cols-[1fr_auto] items-center w-full gap-2 mb-3">
                  <div className="group relative min-w-0 w-fit max-w-full">
                    <h4
                      className="watch-party-title text-gradient-blue"
                      tabIndex={0}
                      dangerouslySetInnerHTML={{
                        __html: sanitizedWatchPartyTitle,
                      }}
                    />
                    {watchPartyTitleTooltip && (
                      <span className="pointer-events-none absolute left-0 top-full z-[999] max-w-[min(420px,calc(100vw-40px))] border border-white/50 rounded-md bg-[#3d026c] px-3 py-2 text-sm text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100">
                        {watchPartyTitleTooltip}
                      </span>
                    )}
                  </div>
                  <span className="text-white/80 block text-end">
                    {/* {this.props.roomDescription} */}
                    Participants:{' '}
                    <span className="gradient-btn text-black rounded-md py-1 px-3">
                      {this.state.participants.length}
                    </span>
                  </span>
                </div>
              ) : null}
              <div
                className={`grid-rows-[125px_1fr] ${
                  !this.state.fullScreen && isOwner ? 'grid' : 'block'
                }`}
              >
                {!this.state.fullScreen && isOwner && (
                  <div>
                    <div className="flex gap-6">
                      <button
                        className={`text-[18px] ${
                          this.state.activeTab === 'pay'
                            ? 'text-gradient-orange'
                            : 'text-gray-500'
                        }`}
                        onClick={() => this.setState({ activeTab: 'pay' })}
                      >
                        Play with URL
                      </button>
                      <button
                        className={`text-[18px] ${
                          this.state.activeTab === 'other'
                            ? 'text-gradient-orange'
                            : 'text-gray-500'
                        }`}
                        onClick={() => this.setState({ activeTab: 'other' })}
                      >
                        Other Options
                      </button>
                    </div>

                    {this.state.activeTab === 'pay' && isOwner ? (
                      <div className="mb-[10px]">
                        <ComboBox
                          setMedia={this.setMedia}
                          playlistAdd={this.playlistAdd}
                          playlistDelete={this.playlistDelete}
                          playlistMove={this.playlistMove}
                          currentMedia={this.state.currentMedia}
                          getMediaDisplayName={this.getMediaDisplayName}
                          launchMultiSelect={this.launchMultiSelect}
                          streamPath={this.props.streamPath}
                          mediaPath={this.state.mediaPath}
                          disabled={!this.haveLock()}
                          playlist={this.state.playlist}
                          togglePlaylist={this.togglePlaylist}
                        />
                        <Separator />
                      </div>
                    ) : null}

                    <div className="flex gap-1">
                      {/* <Button
                            fluid
                            className="toolButton"
                            icon
                            labelPosition="left"
                            color="red"
                            onClick={this.stopPublishingLocalStream}
                            disabled={sharer?.id !== this.socket?.id}
                          >
                            <Icon name="cancel" />
                            Stop Share
                          </Button> */}
                      {this.localStreamToPublish && (
                        <button
                          className="text-[#ff5b5b] border border-[#ff5b5b] flex items-center justify-center my-4 gap-1 rounded-md py-3 px-4 text-[18px] duration-200  hover:opacity-70 w-full"
                          onClick={this.stopPublishingLocalStream}
                          disabled={sharer?.id !== this.socket?.id}
                        >
                          <svg
                            viewBox="-0.625 -0.625 20 20"
                            fill="none"
                            stroke="#ff5b5b"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            height="20"
                            width="20"
                          >
                            <path
                              d="M10.15625 2.34375H3.125a1.5625 1.5625 0 0 0 -1.5625 1.5625v7.8125a1.5625 1.5625 0 0 0 1.5625 1.5625h12.5a1.5625 1.5625 0 0 0 1.5625 -1.5625v-2.34375"
                              strokeWidth="1.25"
                            ></path>
                            <path
                              d="M6.25 16.40625h6.25"
                              strokeWidth="1.25"
                            ></path>
                            <path
                              d="M9.375 13.28125v3.125"
                              strokeWidth="1.25"
                            ></path>
                            <path
                              d="m13.28125 6.25 3.90625 -3.90625"
                              strokeWidth="1.25"
                            ></path>
                            <path
                              d="M13.28125 2.34375h3.90625v3.90625"
                              strokeWidth="1.25"
                            ></path>
                          </svg>
                          Stop Share
                        </button>
                      )}

                      {isOwner &&
                        !this.localStreamToPublish &&
                        this.state.activeTab === 'other' &&
                        !sharer &&
                        !this.playingVBrowser() && (
                          <button
                            title="Share a tab or an application."
                            className="gradient-btn text-black flex items-center justify-center my-4 gap-1 rounded-md py-3 px-4 text-[18px] duration-200  hover:opacity-70 w-full"
                            onClick={() => {
                              // this.startScreenShare(true)
                              this.setState({
                                isScreenShareModalOpen: true,
                              });
                            }}
                          >
                            <svg
                              viewBox="-0.625 -0.625 20 20"
                              fill="none"
                              stroke="#33333d"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              height="20"
                              width="20"
                            >
                              <path
                                d="M10.15625 2.34375H3.125a1.5625 1.5625 0 0 0 -1.5625 1.5625v7.8125a1.5625 1.5625 0 0 0 1.5625 1.5625h12.5a1.5625 1.5625 0 0 0 1.5625 -1.5625v-2.34375"
                                strokeWidth="1.25"
                              ></path>
                              <path
                                d="M6.25 16.40625h6.25"
                                strokeWidth="1.25"
                              ></path>
                              <path
                                d="M9.375 13.28125v3.125"
                                strokeWidth="1.25"
                              ></path>
                              <path
                                d="m13.28125 6.25 3.90625 -3.90625"
                                strokeWidth="1.25"
                              ></path>
                              <path
                                d="M13.28125 2.34375h3.90625v3.90625"
                                strokeWidth="1.25"
                              ></path>
                            </svg>
                            Screen share
                          </button>
                        )}
                      {/* {!this.localStreamToPublish &&
                            !sharer &&
                            !this.playingVBrowser() && (
                              <Popup
                                content="Launch a shared virtual browser"
                                trigger={
                                  <Button
                                    fluid
                                    className="toolButton"
                                    disabled={!this.haveLock()}
                                    icon
                                    labelPosition="left"
                                    color="green"
                                    onClick={() => {
                                      this.setState({
                                        isVBrowserModalOpen: true,
                                      });
                                    }}
                                  >
                                    <Icon name="desktop" />
                                    VBrowser
                                  </Button>
                                }
                              />
                            )}
                          {this.playingVBrowser() && (
                            <Popup
                              content="Choose the person controlling the VBrowser"
                              trigger={
                                <Dropdown
                                  icon="keyboard"
                                  labeled
                                  className="icon"
                                  style={{ height: '36px' }}
                                  button
                                  value={this.state.controller}
                                  placeholder="No controller"
                                  clearable
                                  onChange={this.changeController}
                                  selection
                                  disabled={!this.haveLock()}
                                  options={this.state.participants.map((p) => ({
                                    text: this.state.nameMap[p.id] || p.id,
                                    value: p.id,
                                  }))}
                                ></Dropdown>
                              }
                            />
                          )}
                          {this.playingVBrowser() && (
                            <Dropdown
                              icon="desktop"
                              labeled
                              className="icon"
                              style={{ height: '36px' }}
                              button
                              disabled={!this.haveLock()}
                              value={this.state.vBrowserResolution}
                              onChange={(_e, data) =>
                                this.setState({
                                  vBrowserResolution: data.value as string,
                                })
                              }
                              selection
                              options={[
                                {
                                  text: '1080p (Plus only)',
                                  value: '1920x1080@30',
                                  disabled: !this.state.isVBrowserLarge,
                                },
                                {
                                  text: '720p',
                                  value: '1280x720@30',
                                },
                                {
                                  text: '576p',
                                  value: '1024x576@60',
                                },
                                {
                                  text: '486p',
                                  value: '864x486@60',
                                },
                                {
                                  text: '360p',
                                  value: '640x360@60',
                                },
                              ]}
                            ></Dropdown>
                          )}
                          {this.playingVBrowser() && (
                            <Button
                              fluid
                              className="toolButton"
                              icon
                              labelPosition="left"
                              color="red"
                              disabled={!this.haveLock()}
                              onClick={this.stopVBrowser}
                            >
                              <Icon name="cancel" />
                              Stop VBrowser
                            </Button>
                          )}
                          {!this.localStreamToPublish &&
                            !sharer &&
                            !this.playingVBrowser() && (
                              <Popup
                                content="Stream your own video file"
                                trigger={
                                  <Button
                                    fluid
                                    className="toolButton"
                                    disabled={!this.haveLock()}
                                    icon
                                    labelPosition="left"
                                    onClick={() => {
                                      this.setState({
                                        isFileShareModalOpen: true,
                                      });
                                    }}
                                  >
                                    <Icon name="file" />
                                    File
                                  </Button>
                                }
                              />
                            )} */}
                      {false && (
                        <SearchComponent
                          setMedia={this.setMedia}
                          playlistAdd={this.playlistAdd}
                          type={'youtube'}
                          streamPath={this.props.streamPath}
                          disabled={!this.haveLock()}
                        />
                      )}
                      {Boolean(this.props.streamPath) && (
                        <SearchComponent
                          setMedia={this.setMedia}
                          playlistAdd={this.playlistAdd}
                          type={'stream'}
                          streamPath={this.props.streamPath}
                          launchMultiSelect={this.launchMultiSelect}
                          disabled={!this.haveLock()}
                        />
                      )}
                    </div>
                    <Separator />
                  </div>
                )}

                <div
                  className={`relative flex flex-col ${
                    !this.state.fullScreen && isOwner
                      ? 'h-[calc(100dvh-255px)]'
                      : 'h-[calc(100dvh-150px)]'
                  }`}
                >
                  {(this.state.loading ||
                    !this.state.currentMedia ||
                    this.state.nonPlayableMedia) && (
                    <div
                      id="loader"
                      className="bg-[#3d026c] flex max-md:z-[1] items-center justify-center absolute top-0 left-0 size-full rounded-md"
                    >
                      {this.state.loading && (
                        <Dimmer active>
                          <Loader>
                            {this.playingVBrowser()
                              ? 'Launching virtual browser. This can take up to a minute.'
                              : ''}
                          </Loader>
                        </Dimmer>
                      )}
                      {!this.state.loading && !this.state.currentMedia && (
                        <div className="bg-[#3d026c] flex items-center justify-center absolute top-0 left-0 size-full rounded-lg">
                          <div className="flex flex-col justify-center items-center gap-7 p-3">
                            <img src={'/vector.png'} />
                            <span className="text-gradient-orange text-xl md:text-3xl text-center">
                              You are not watching anything
                            </span>
                          </div>
                        </div>

                        // <Message
                        //   color="yellow"
                        //   icon="hand point up"
                        //   header="You're not watching anything!"
                        //   content="Pick something to watch above."
                        // />
                      )}
                      {!this.state.loading && this.state.nonPlayableMedia && (
                        <div className="fixed inset-0 z-[9999] size-full bg-black/50 grid place-items-center">
                          <div className="gradient-modal rounded-xl max-w-xl p-5 text-center relative">
                            <button
                              onClick={() => {
                                this.setState({ nonPlayableMedia: false });
                              }}
                              className="absolute right-3 top-3"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="#979797"
                                width={30}
                                height={30}
                              >
                                <path d="M12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22ZM12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20ZM12 10.5858L14.8284 7.75736L16.2426 9.17157L13.4142 12L16.2426 14.8284L14.8284 16.2426L12 13.4142L9.17157 16.2426L7.75736 14.8284L10.5858 12L7.75736 9.17157L9.17157 7.75736L12 10.5858Z"></path>
                              </svg>
                            </button>
                            <svg
                              className="mx-auto"
                              xmlns="http://www.w3.org/2000/svg"
                              width="70"
                              height="70"
                              viewBox="0 0 100 100"
                            >
                              <g>
                                <path
                                  d="M50 98C23.533 98 2 76.467 2 50S23.533 2 50 2s48 21.533 48 48-21.533 48-48 48zm0-90C26.841 8 8 26.841 8 50s18.841 42 42 42 42-18.841 42-42S73.159 8 50 8zm22.342 62.684a3 3 0 0 0 1.342-4.025C73.404 66.1 66.663 53 50 53S26.596 66.1 26.316 66.658a2.994 2.994 0 0 0 1.332 4.012 3.008 3.008 0 0 0 4.028-1.315C31.893 68.932 37.13 59 50 59s18.107 9.932 18.316 10.342A2.995 2.995 0 0 0 71.003 71c.45 0 .908-.101 1.339-.316zM65 44c-3.309 0-6-2.691-6-6s2.691-6 6-6 6 2.691 6 6-2.691 6-6 6zm-30 0c-3.309 0-6-2.691-6-6s2.691-6 6-6 6 2.691 6 6-2.691 6-6 6z"
                                  fill="#ef4444"
                                ></path>
                              </g>
                            </svg>
                            <h3 className="text-gradient-blue text-3xl my-4">
                              It doesn't look like this is a media file!
                            </h3>
                            <h6 className="text-gradient-blue text-[20px]">
                              Maybe you meant to launch a VBrowser if you're
                              trying to visit a web page?
                            </h6>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <div
                    className={`gradient-modal h-full overflow-hidden ${
                      this.state.fakeFullScreen
                        ? 'fake-fullscreen'
                        : 'rounded-lg'
                    }`}
                    id="youtube"
                  >
                    <div
                      id="blocker"
                      style={!isOwner ? { pointerEvents: 'none' } : {}}
                      className={`h-full ${
                        this.usingYoutube() ? 'block' : 'hidden'
                      }`}
                    >
                      <iframe
                        title="YouTube"
                        id="leftYt"
                        className={`bg-[#3d026c] size-full rounded-md ${
                          this.usingYoutube() && !this.state.loading
                            ? 'block'
                            : 'hidden'
                        }`}
                        allowFullScreen
                        frameBorder="0"
                        allow="autoplay; encrypted-media"
                        src="https://www.youtube-nocookie.com/embed/?enablejsapi=1&controls=0&rel=0"
                      />
                    </div>
                    {this.socket?.id &&
                      this.playingVBrowser() &&
                      this.getVBrowserPass() &&
                      this.getVBrowserHost() && (
                        <VBrowser
                          username={this.socket.id}
                          password={this.getVBrowserPass()}
                          hostname={this.getVBrowserHost()}
                          controlling={this.state.controller === this.socket.id}
                          resolution={this.state.vBrowserResolution}
                          doPlay={this.doPlay}
                          setResolution={(data: string) =>
                            this.setState({ vBrowserResolution: data })
                          }
                        />
                      )}
                    <video
                      className={`size-full ${
                        ((this.usingNative() && !this.state.loading) ||
                          this.state.fullScreen) &&
                        !this.playingVBrowser()
                          ? 'block'
                          : 'hidden'
                      }`}
                      id="leftVideo"
                      style={{
                        objectFit: 'contain',
                        backfaceVisibility: 'hidden',
                      }}
                      onEnded={this.onVideoEnded}
                      playsInline
                      onClick={this.togglePlay}
                    ></video>
                    {this.state.currentMedia && controls}
                  </div>
                </div>

                {Boolean(this.state.total) && (
                  <div className="text-white text-center text-[11px] font-bold -mt-2">
                    {/* <Progress
                          size="tiny"
                          color="green"
                          inverted
                          value={this.state.downloaded}
                          total={this.state.total}
                          // indicating
                          label={}
                        ></Progress> */}
                    {Math.min(
                      (this.state.downloaded / this.state.total) * 100,
                      100
                    ).toFixed(2) +
                      '% - ' +
                      formatSpeed(this.state.speed) +
                      ' - ' +
                      this.state.connections +
                      ' connections'}
                  </div>
                )}
                <button
                  className="text-3xl size-16 gradient-btn text-black flex items-center justify-center rounded-full fixed bottom-32 right-5 z-[999] lg:hidden hover:opacity-50"
                  onClick={() => {
                    this.setState({ isChatBoxOpen: true });
                  }}
                >
                  <i className="ri-message-2-fill"></i>
                </button>
              </div>
            </div>

            <div
              className={`max-lg:fixed right-0 top-0 max-lg:border-l border-white/15 size-full max-lg:max-w-xl max-lg:z-[9999] max-lg:p-5 max-lg:bg-black duration-200 ${
                this.state.isChatBoxOpen
                  ? 'max-lg:translate-x-0'
                  : 'max-lg:translate-x-full'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    className={`gradient-orange-btn relative z-0 rounded-full px-4 text-[16px] py-2 ${
                      this.state.currentTab === 'chat' ? 'activeButton' : ''
                    }`}
                    onClick={() => {
                      this.setState({ currentTab: 'chat', unreadCount: 0 });
                    }}
                  >
                    <span>Chat</span>
                  </button>
                  <button
                    type="button"
                    className={`gradient-orange-btn relative z-0 rounded-full px-4 text-[16px] py-2 ${
                      this.state.currentTab === 'settings' ? 'activeButton' : ''
                    }`}
                    onClick={() => this.setState({ currentTab: 'settings' })}
                  >
                    <span>Settings</span>
                  </button>
                </div>
                <button
                  className="text-white text-2xl lg:hidden"
                  onClick={() => {
                    this.setState({ isChatBoxOpen: !this.state.isChatBoxOpen });
                  }}
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>
              <Chat
                chat={this.state.chat}
                nameMap={this.state.nameMap}
                pictureMap={this.state.pictureMap}
                socket={this.socket}
                scrollTimestamp={this.state.scrollTimestamp}
                getMediaDisplayName={this.getMediaDisplayName}
                hide={this.state.currentTab !== 'chat' || !displayRightContent}
                isChatDisabled={this.state.isChatDisabled}
                owner={this.state.owner}
                user={this.props.user}
                ref={this.chatRef}
                isLiveHls={this.state.isLiveHls}
                roomId={this.state.roomId}
                participants={this.state.participants}
                coHosts={this.state.coHosts}
                allJoinedParticipants={this.state.allJoinedParticipants}
                handleChatLikeClick={(index: number, subIndex: number) => {
                  this.setState({
                    selectedMessageIndex: index,
                    selectedSubMessageIndex: subIndex,
                  });
                }}
              />
              {this.socket && this.state.state === 'connected' && (
                <VideoChat
                  socket={this.socket}
                  participants={this.state.participants}
                  nameMap={this.state.nameMap}
                  pictureMap={this.state.pictureMap}
                  tsMap={this.state.tsMap}
                  rosterUpdateTS={this.state.rosterUpdateTS}
                  hide={
                    this.state.currentTab !== 'people' || !displayRightContent
                  }
                  owner={this.state.owner}
                  user={this.props.user}
                  beta={this.props.beta}
                  getLeaderTime={this.getLeaderTime}
                  roomId={this.state.roomId}
                />
              )}
              <SettingsTab
                coHosts={this.state.coHosts}
                nameMap={this.state.nameMap}
                pictureMap={this.state.pictureMap}
                hide={this.state.currentTab !== 'settings'}
                user={this.props.user as unknown as User}
                roomLock={this.state.roomLock}
                setRoomLock={this.setRoomLock}
                socket={this.socket}
                isSubscriber={this.props.isSubscriber}
                roomId={this.state.roomId}
                isChatDisabled={this.state.isChatDisabled}
                setIsChatDisabled={this.setIsChatDisabled}
                owner={this.state.owner}
                setOwner={this.setOwner}
                vanity={this.state.vanity}
                setVanity={this.setVanity}
                roomLink={this.state.roomLink}
                password={this.state.password}
                setPassword={this.setPassword}
                clearChat={this.clearChat}
                roomTitle={this.state.roomTitle}
                setRoomTitle={this.setRoomTitle}
                roomDescription={this.state.roomDescription}
                setRoomDescription={this.setRoomDescription}
                roomTitleColor={this.state.roomTitleColor}
                setRoomTitleColor={this.setRoomTitleColor}
                mediaPath={this.state.mediaPath}
                setMedia={this.setMedia}
                setMediaPath={this.setMediaPath}
                participants={this.state.participants}
                settings={this.state.settings}
                getRoomState={() => {
                  this.socket &&
                    this.socket?.on('CMD:getRoomState', this.handleRoomState);
                }}
                handleClickOnNo={() => {
                  this.getWatchpartyDetails(); // for get latest details
                }}
                watchPartyDetails={this.state.watchPartyDetails}
                showAdminLeaveModal={this.state.showAdminLeaveModal}
                handleToggleAdminLeaveModal={() => {
                  this.setState({
                    showAdminLeaveModal: !this.state.showAdminLeaveModal,
                  });
                }}
              />
            </div>
          </div>
        </div>
        <HostLeaveDialog
          participants={this.state.participants}
          socket={this.socket}
          open={this.state.showAdminLeaveModal}
          setOpen={() => {
            this.setState({
              showAdminLeaveModal: !this.state.showAdminLeaveModal,
            });
          }}
          roomId={this.state.roomId}
          nameMap={this.state.nameMap}
          pictureMap={this.state.pictureMap}
          setMedia={this.setMedia}
          redirectToWatchPartyDomain={this.redirectToWatchPartyDomain}
          handleClickOnNo={() => {
            this.getWatchpartyDetails(); // for get latest details
          }}
          owner={owner}
        />
        <LeaveDialog
          participants={this.state.participants}
          socket={this.socket}
          open={this.state.showLeaveModal}
          setOpen={() => {
            this.setState({
              showLeaveModal: !this.state.showLeaveModal,
            });
          }}
          roomId={this.state.roomId}
          nameMap={this.state.nameMap}
          pictureMap={this.state.pictureMap}
          setMedia={this.setMedia}
          redirectToWatchPartyDomain={this.redirectToWatchPartyDomain}
          handleClickOnNo={() => {
            this.getWatchpartyDetails();
          }}
          owner={owner}
          user={this.props.user as any}
        />
        <Playlist
          playlist={this.state.playlist}
          showPlaylist={this.state.showPlaylist}
          setMedia={this.setMedia}
          removeFromPlaylist={this.removeFromPlaylist}
          togglePlaylist={this.togglePlaylist}
        />
      </React.Fragment>
    );
  }
}

export const Separator = () => <div className="h-1 flex-shrink-0" />;
